/**
 * Container - Viewport Boundary Primitive
 *
 * (Renamed from AppContainer for industry-standard naming)
 *
 * Provides proper height constraints that work in both modes:
 * - Standalone: Uses 100vh to fill the viewport
 * - Bundle: Uses 100% to fill the bundle overlay container
 *
 * This eliminates the common issue where apps use min-height: 100vh
 * which breaks when rendered inside a bundle overlay.
 *
 * @example
 * ```tsx
 * <Container>
 *   <YourAppContent />
 * </Container>
 * ```
 */

import { ReactNode } from 'react';
import { isBundleMode } from '../../utils/bundleMode';
import './Container.css';

export interface ContainerProps {
  /**
   * Content to render inside the container
   */
  children: ReactNode;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Flex direction
   * @default 'column'
   */
  direction?: 'row' | 'column';

  /**
   * Background color or gradient
   */
  background?: string;
}

/**
 * Container component - viewport boundary primitive
 */
export function Container({
  children,
  className = '',
  direction = 'column',
  background,
}: ContainerProps) {
  const isBundle = isBundleMode();

  return (
    <div
      className={`container ${isBundle ? 'container--bundle' : 'container--standalone'} ${className}`}
      style={{
        flexDirection: direction,
        ...(background ? { background } : {}),
      }}
    >
      {children}
    </div>
  );
}

export default Container;
